// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api/client'
import {
  ArrowLeft, Loader2, Wifi, WifiOff, ArrowUpCircle, Key, Server, ShieldAlert,
  ClipboardList, Gavel
} from 'lucide-react'

interface Sev { total: number; critical: number; high: number; medium: number; low: number; has_breakdown: boolean }
interface Poam { total: number; open: number; overdue: number; completed: number; on_time_pct: number | null; open_critical: number; open_high: number; present: boolean }
interface Bod { buckets: Record<string, number>; labels: Record<string, string>; overdue_sla: number; open_forensic_cases: number; total_classified: number; present: boolean }
interface Lic {
  id: string; license_key_masked: string | null; license_type: string
  is_active: boolean; is_revoked: boolean; is_expired: boolean; in_grace_period: boolean
  expires_at: string | null; days_until_expiry: number | null; expiring_soon: boolean
  max_users: number; max_endpoints: number; max_vulnerabilities: number
}
interface Inst {
  id: string; installation_id: string; hostname: string | null; platform: string | null
  current_version: string | null; is_online: boolean; update_available: boolean
  last_heartbeat: string | null; registered_at: string | null
}
interface Detail {
  tenant_id: string; name: string; licenses: Lic[]; installations: Inst[]
  vulnerabilities: Sev; poam: Poam; bod: Bod; last_seen: string | null; latest_version: string | null
}

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '—')

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'danger' }) {
  return (
    <div className="rounded border border-dark-border bg-gray-900/40 p-2 text-center">
      <div className={`text-lg font-bold ${tone === 'danger' ? 'text-red-400' : 'text-gray-100'}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

function SeverityBar({ sev }: { sev: Sev }) {
  const parts = [
    { n: sev.critical, c: 'bg-red-500', t: 'text-red-400', label: 'Critical' },
    { n: sev.high, c: 'bg-orange-500', t: 'text-orange-400', label: 'High' },
    { n: sev.medium, c: 'bg-yellow-500', t: 'text-yellow-400', label: 'Medium' },
    { n: sev.low, c: 'bg-sky-600', t: 'text-sky-400', label: 'Low' },
  ]
  const total = parts.reduce((a, p) => a + p.n, 0) || 1
  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-800">
        {parts.map((p) => p.n > 0 && (
          <div key={p.label} className={p.c} style={{ width: `${(p.n / total) * 100}%` }} title={`${p.label}: ${p.n}`} />
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {parts.map((p) => (
          <div key={p.label} className="rounded border border-dark-border bg-gray-900/40 p-2">
            <div className={`text-lg font-bold ${p.t}`}>{p.n.toLocaleString()}</div>
            <div className="text-xs text-gray-500">{p.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function MSPCustomerDetail() {
  const { tenantId } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<Detail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/portal/msp/customers/${tenantId}`)
        setDetail(res.data)
      } catch (e: any) {
        if (e?.response?.status === 404) setNotFound(true)
        else console.error('Failed to load customer detail:', e)
      } finally {
        setLoading(false)
      }
    })()
  }, [tenantId])

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary-500" /></div>
  }
  if (notFound || !detail) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/msp')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to fleet
        </button>
        <div className="rounded-lg border border-dark-border bg-dark-card p-8 text-center text-gray-400">
          Customer not found or not managed by your account.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/msp')} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Back to fleet
      </button>

      <div>
        <h1 className="text-2xl font-bold">{detail.name}</h1>
        <p className="mt-1 text-sm text-gray-500">Last seen {fmt(detail.last_seen)}</p>
      </div>

      {/* Vulnerabilities */}
      <div className="rounded-lg border border-dark-border bg-dark-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-2 font-medium"><ShieldAlert className="h-5 w-5 text-primary-500" /> Vulnerability summary</span>
          <span className="text-sm text-gray-400">{detail.vulnerabilities.total.toLocaleString()} total</span>
        </div>
        {detail.vulnerabilities.has_breakdown || detail.vulnerabilities.total > 0
          ? <SeverityBar sev={detail.vulnerabilities} />
          : <p className="text-sm text-gray-500">No vulnerability data reported yet (customer instance may predate severity reporting).</p>}
      </div>

      {/* POA&M */}
      {detail.poam.present && (
        <div className="rounded-lg border border-dark-border bg-dark-card p-5">
          <h2 className="mb-3 flex items-center gap-2 font-medium"><ClipboardList className="h-5 w-5 text-primary-500" /> POA&amp;M</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Open" value={detail.poam.open} />
            <Stat label="Overdue" value={detail.poam.overdue} tone={detail.poam.overdue ? 'danger' : undefined} />
            <Stat label="Completed" value={detail.poam.completed} />
            <Stat label="On-time" value={detail.poam.on_time_pct != null ? `${detail.poam.on_time_pct}%` : '—'} />
          </div>
          <div className="mt-3 flex gap-4 text-sm">
            <span className="text-red-400">{detail.poam.open_critical} critical open</span>
            <span className="text-orange-400">{detail.poam.open_high} high open</span>
          </div>
        </div>
      )}

      {/* BOD 26-04 */}
      {detail.bod.present && (
        <div className="rounded-lg border border-dark-border bg-dark-card p-5">
          <h2 className="mb-3 flex items-center gap-2 font-medium"><Gavel className="h-5 w-5 text-primary-500" /> BOD 26-04</h2>
          {Object.keys(detail.bod.buckets).length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Object.entries(detail.bod.buckets).map(([k, v]) => (
                <Stat key={k} label={detail.bod.labels[k] || k} value={v} />
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <span className={detail.bod.overdue_sla ? 'text-red-400' : 'text-gray-400'}>{detail.bod.overdue_sla} SLA overdue</span>
            <span className="text-gray-400">{detail.bod.open_forensic_cases} open forensic case{detail.bod.open_forensic_cases !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {/* Licenses */}
      <div className="rounded-lg border border-dark-border bg-dark-card p-5">
        <h2 className="mb-3 flex items-center gap-2 font-medium"><Key className="h-5 w-5 text-primary-500" /> License</h2>
        <div className="space-y-3">
          {detail.licenses.length === 0 && <p className="text-sm text-gray-500">No license on record.</p>}
          {detail.licenses.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 rounded border border-dark-border bg-gray-900/40 p-3">
              <div>
                <div className="font-mono text-sm">{l.license_key_masked || '—'}</div>
                <div className="mt-1 text-xs capitalize text-gray-400">
                  {l.license_type} · {l.max_users} users · {l.max_endpoints} endpoints
                </div>
              </div>
              <div className="text-right text-sm">
                {l.is_revoked ? <span className="text-red-400">Revoked</span>
                  : l.is_expired ? <span className="text-red-400">Expired</span>
                  : l.expiring_soon ? <span className="text-amber-400">Expires in {l.days_until_expiry}d</span>
                  : <span className="text-green-400">Active</span>}
                <div className="text-xs text-gray-500">{l.expires_at ? `until ${new Date(l.expires_at).toLocaleDateString()}` : 'no expiry'}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Installations */}
      <div className="rounded-lg border border-dark-border bg-dark-card p-5">
        <h2 className="mb-3 flex items-center gap-2 font-medium"><Server className="h-5 w-5 text-primary-500" /> Installations ({detail.installations.length})</h2>
        {detail.installations.length === 0 ? (
          <p className="text-sm text-gray-500">No installations registered.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border text-left text-xs uppercase text-gray-500">
                  <th className="pb-2 pr-4">Host</th>
                  <th className="pb-2 pr-4">Platform</th>
                  <th className="pb-2 pr-4">Version</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2">Last heartbeat</th>
                </tr>
              </thead>
              <tbody>
                {detail.installations.map((i) => (
                  <tr key={i.id} className="border-b border-dark-border/50">
                    <td className="py-2 pr-4">{i.hostname || i.installation_id.slice(0, 12)}</td>
                    <td className="py-2 pr-4 text-gray-400">{i.platform || '—'}</td>
                    <td className="py-2 pr-4">
                      <span className="flex items-center gap-1">
                        {i.current_version || '—'}
                        {i.update_available && <ArrowUpCircle className="h-3.5 w-3.5 text-amber-400" />}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      {i.is_online
                        ? <span className="flex items-center gap-1 text-green-400"><Wifi className="h-3.5 w-3.5" /> Online</span>
                        : <span className="flex items-center gap-1 text-gray-500"><WifiOff className="h-3.5 w-3.5" /> Offline</span>}
                    </td>
                    <td className="py-2 text-gray-400">{fmt(i.last_heartbeat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-gray-500">
          Need to see individual findings, POA&amp;M, or compliance? Sign in to this customer's ETS instance as an admin.
        </p>
      </div>
    </div>
  )
}
